/**
 * Benefits section highlighting platform advantages.
 *
 * Renders a responsive grid of benefit items, each with an inline SVG icon,
 * title, and description. The section heading supports brandName interpolation.
 * Data-driven via the {@link BENEFITS} array.
 *
 * @module landing/sections/BenefitsSection
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { ResponsiveContainer } from '../../../shared/src/responsive/ResponsiveContainer';
import { ResponsiveGrid } from '../../../shared/src/responsive/ResponsiveGrid';
import { useTheme } from '../../../shared/src/theme/ThemeContext';
import { SectionHeading } from '../components/SectionHeading';
import { BENEFITS } from '../content/benefits';

/**
 * Benefits section with icon grid and brand-interpolated heading.
 */
export function BenefitsSection(): React.JSX.Element {
  const { t } = useTranslation('landing');
  const { branding } = useTheme();

  return (
    <ResponsiveContainer>
      <SectionHeading
        title={t('benefits.heading', { brandName: branding.displayName })}
        subtitle={t('benefits.subheading')}
      />
      <ResponsiveGrid sm={2} lg={4}>
        {BENEFITS.map((item) => (
          <div key={item.id} className="landing-benefit">
            <div className="landing-benefit__icon" aria-hidden="true">
              {item.icon()}
            </div>
            <h3 className="landing-benefit__title">
              {t(`benefits.${item.i18nKey}.title`)}
            </h3>
            <p className="landing-benefit__description">
              {t(`benefits.${item.i18nKey}.description`)}
            </p>
          </div>
        ))}
      </ResponsiveGrid>
    </ResponsiveContainer>
  );
}
